import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

const AboutPage = () => {
  const imageRef = useRef(null);

  useEffect(() => {
    const image = imageRef.current;

    // Subtle floating animation for the profile image
    gsap.to(image, {
      y: -10,
      duration: 4,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
    });

    return () => {
      gsap.killTweensOf(image);
    };
  }, []);

  return (
    <motion.div
      className="relative min-h-screen px-4 py-32"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Profile Image */}
          <motion.div
            ref={imageRef}
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div
              className="relative w-80 h-80 mx-auto rounded-full overflow-hidden"
              style={{
                border: '2px solid rgba(212, 175, 55, 0.4)',
                boxShadow: '0 0 60px rgba(212, 175, 55, 0.3), 0 20px 60px rgba(0, 0, 0, 0.4)',
              }}
            >
              <img 
                src="/images/about/ragupathi.jpg" 
                alt="Ragupathi Kannan" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Decorative rings */}
            <div
              className="absolute inset-0 w-80 h-80 mx-auto rounded-full"
              style={{
                border: '1px solid rgba(212, 175, 55, 0.15)',
                transform: 'scale(1.2)',
              }}
            />
            <div
              className="absolute inset-0 w-80 h-80 mx-auto rounded-full"
              style={{
                border: '1px solid rgba(212, 175, 55, 0.08)',
                transform: 'scale(1.4)',
              }}
            />
          </motion.div>

          {/* Text Content */}
          <motion.div
            className="order-1 lg:order-2 text-center lg:text-left"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <h1
              className="text-4xl md:text-5xl font-display tracking-wider mb-2"
              style={{
                color: '#d4af37',
                textShadow: '0 0 40px rgba(212, 175, 55, 0.4)',
              }}
            >
              ரகுபதி கண்ணன்
            </h1>
            <p
              className="text-xl font-body mb-6"
              style={{ color: 'rgba(212, 175, 55, 0.7)' }}
            >
              Ragupathi Kannan
            </p>
            <p
              className="text-xl font-body italic mb-8"
              style={{ color: 'rgba(192, 192, 192, 0.9)' }}
            >
              ஸ்ரீ செல்வகணபதி ஜோதிடம், சேலம்
            </p>
            <p
              className="font-body text-lg leading-relaxed mb-6"
              style={{ color: 'rgba(192, 192, 192, 0.7)' }}
            >
              25 ஆண்டுகளுக்கும் மேலான அனுபவத்துடன், பாரம்பரிய வேத ஜோதிடத்தில் 
              நிபுணத்துவம் பெற்ற ஜோதிடர். சேலத்தை சேர்ந்த எங்கள் குடும்பம் பல தலைமுறைகளாக 
              ஜோதிட சேவையில் ஈடுபட்டு வருகிறது.
            </p>
            <p
              className="font-body text-lg leading-relaxed"
              style={{ color: 'rgba(192, 192, 192, 0.7)' }}
            >
              ஜாதக கணிப்பு, திருமண பொருத்தம், தொழில் ஆலோசனை, கிரக தோஷ பரிகாரம் 
              ஆகியவற்றில் சிறப்பு கவனம் செலுத்துகிறோம். உங்கள் வாழ்க்கையின் 
              ஒவ்வொரு கட்டத்திலும் சரியான வழிகாட்டுதல் வழங்குவோம்.
            </p>
          </motion.div>
        </div>

        {/* Philosophy Section */}
        <motion.div
          className="text-center mb-24"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2
            className="text-3xl font-display tracking-wider mb-8"
            style={{ color: '#d4af37' }}
          >
            எங்கள் நோக்கம்
          </h2>
          <p
            className="font-body text-xl italic max-w-3xl mx-auto leading-relaxed"
            style={{ color: 'rgba(192, 192, 192, 0.8)' }}
          >
            "நட்சத்திரங்கள் உங்கள் விதியை நிர்ணயிக்கவில்லை; அவை உங்கள் பாதையை 
            காட்டுகின்றன. சரியான வழிகாட்டுதலுடன், நீங்கள் உங்கள் வாழ்க்கையை 
            சிறப்பாக மாற்றிக்கொள்ளலாம்."
          </p>
        </motion.div>

        {/* Credentials */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {[
            { number: '25+', label: 'ஆண்டுகள் அனுபவம்', sublabel: 'Years Experience' },
            { number: '10000+', label: 'ஜாதகங்கள் கணிப்பு', sublabel: 'Horoscopes Read' },
            { number: 'சேலம்', label: 'தமிழ்நாடு', sublabel: 'Salem, Tamil Nadu' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center p-8 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                border: '1px solid rgba(212, 175, 55, 0.15)',
              }}
              whileHover={{
                borderColor: 'rgba(212, 175, 55, 0.3)',
                boxShadow: '0 0 30px rgba(212, 175, 55, 0.1)',
              }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="text-4xl md:text-5xl font-display mb-2"
                style={{ color: '#d4af37' }}
              >
                {stat.number}
              </div>
              <div
                className="text-sm font-display tracking-wider mb-1"
                style={{ color: 'rgba(212, 175, 55, 0.8)' }}
              >
                {stat.label}
              </div>
              <div
                className="text-xs font-body"
                style={{ color: 'rgba(192, 192, 192, 0.5)' }}
              >
                {stat.sublabel}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AboutPage;
